import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { useState } from "react";
import type { ItemStatus } from "@instead/shared";
import { api, ApiRequestError } from "../lib/api.ts";
import { moodLabel, useT } from "../lib/i18n.ts";
import { catLabel } from "../components/DealCard.tsx";
import { Cover } from "../components/Cover.tsx";
import { Ic } from "../components/Icons.tsx";
import { ItemForm } from "../components/ItemForm.tsx";
import { Markdown } from "../components/Markdown.tsx";
import { fmtDuration, Toast, useToast } from "../components/Ui.tsx";

/** Item view (round 4): Open, progress, Pin, note, Edit. Delete lives on the edit form. */
export function ItemDetail() {
  const t = useT();
  const { id } = useParams({ from: "/item/$id" });
  const search = useSearch({ strict: false }) as { edit?: string };
  const qc = useQueryClient();
  const nav = useNavigate();
  const { data: item } = useQuery({ queryKey: ["items", id], queryFn: () => api.items.get(id) });
  const { data: catData } = useQuery({ queryKey: ["categories"], queryFn: api.categories.list });
  const { data: moodsData } = useQuery({ queryKey: ["moods"], queryFn: api.moods.list });
  const [annotation, setAnnotation] = useState<string | null>(null);
  const [toast, say] = useToast();
  const editing = search.edit === "1";

  const update = useMutation({
    mutationFn: (patch: Parameters<typeof api.items.update>[1]) => api.items.update(id, patch),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["items"] });
      say(t("saved"));
    },
    onError: (e) => {
      if (e instanceof ApiRequestError && e.code === "OFFLINE_QUEUED") say(t("saved")); // invisible offline (round 2)
    },
  });

  if (!item) return null;
  if (editing) {
    return <ItemForm item={item} onDone={() => void nav({ to: "/item/$id", params: { id }, search: {} as never })} />;
  }

  const cats = catData?.categories ?? [];
  const cat = cats.find((c) => c.id === item.categoryId);
  const moods = (moodsData?.moods ?? []).filter((m) => item.moods.includes(m.key)).map(moodLabel);
  const note = annotation ?? item.annotation;
  const meta = [
    fmtDuration(item.durationMin, item.durationMaxMin),
    cat ? catLabel(cat, t).toLowerCase() : "",
    moods.join(", "),
    item.orientation ? t(`orient.${item.orientation}`) : "",
  ].filter(Boolean).join(" · ");

  const open = () => void api.items.open(id).then(() => qc.invalidateQueries({ queryKey: ["items"] }));

  return (
    <article style={{ display: "flex", flexDirection: "column", gap: "var(--sp-3)" }}>
      <div className="screen-head" style={{ marginBottom: 0 }}>
        <button className="icon-btn" aria-label={t("back")} onClick={() => history.back()}>{Ic.back()}</button>
        <span style={{ flex: 1 }} />
        <button className="icon-btn accent" onClick={() => nav({ to: "/item/$id", params: { id }, search: { edit: "1" } as never })}>{Ic.edit({ size: 14 })} {t("edit")}</button>
      </div>

      <Cover item={item} size="l" />
      <h1 className="display">{item.title}</h1>
      {meta && <p className="meta">{meta}</p>}

      {item.link && (
        <a className="btn btn-primary" href={item.link} target="_blank" rel="noopener noreferrer" onClick={open}>
          {t("item.open")} {Ic.external({ size: 13 })}
        </a>
      )}

      <div role="group" aria-label={t("add.trackProgress")} className="chip-row">
        {item.tracksProgress &&
          (["unstarted", "in_progress", "done"] as const).map((s) => (
            <button key={s} className="chip" aria-pressed={item.status === s} onClick={() => update.mutate({ status: s as ItemStatus })}>
              {t(`item.status.${s}`)}
            </button>
          ))}
        <button className="chip" aria-pressed={item.pinned} onClick={() => update.mutate({ pinned: !item.pinned })}>
          {item.pinned ? Ic.pinFilled({ size: 13 }) : Ic.pin({ size: 13 })} {t(item.pinned ? "item.pinned" : "item.pin")}
        </button>
      </div>

      {item.description && (
        <section>
          <h2 className="eyebrow">{t("item.description")}</h2>
          <Markdown text={item.description} />
        </section>
      )}

      <section className="field" style={{ marginBottom: 0 }}>
        <label htmlFor="note" className="eyebrow">{t("item.noteLabel")}</label>
        <textarea id="note" className="input note" value={note} onChange={(e) => setAnnotation(e.target.value)}
          onBlur={() => {
            if (annotation !== null && annotation !== item.annotation) update.mutate({ annotation, ifUpdatedAt: item.updatedAt });
          }} />
      </section>

      <Toast msg={toast} />
    </article>
  );
}
